import { jsx } from 'react/jsx-runtime';
import { mergeOverrides } from '../../overrides.js';
import { ProgressBarRounded, ProgressBar as ProgressBar$1 } from 'baseui/progress-bar';
import 'react';
import 'baseui';
import '../../theme/olx-theme/base-theme.js';
import '../../theme/olx-theme/theme.js';
import { useResolvedStyle, mergeStyles } from '../../theme/utils.js';
import '../../theme/types.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const getStyles = (styleProps, element) => {
  const { $theme, $variant, $showLabel, $value } = styleProps;
  const isCondensed = $variant === "condensed";
  switch (element) {
    case "BarContainer":
      return {
        marginLeft: 0,
        marginRight: 0,
        marginTop: 0,
        marginBottom: 0
      };
    case "Bar":
      return {
        height: isCondensed ? "8px" : "24px",
        backgroundColor: isCondensed ? $theme.colors["background-brand-tertiary"] : $theme.colors["background-brand-secondary-light"],
        borderTopLeftRadius: $theme.radii[4],
        borderTopRightRadius: $theme.radii[4],
        borderBottomLeftRadius: $theme.radii[4],
        borderBottomRightRadius: $theme.radii[4]
      };
    case "BarProgress":
      if (!$showLabel || $variant !== "normal" || !$value) {
        return {};
      }
      return __spreadProps(__spreadValues({}, $theme.typography.p1), {
        color: $theme.colors["text-global-primary"],
        position: "relative",
        ":after": {
          position: "absolute",
          content: $value > 5 ? `"${$value}%"` : "",
          right: $theme.space[10]
        }
      });
    case "Label":
      return __spreadProps(__spreadValues({}, $theme.typography.p4), {
        textAlign: "left"
      });
    case "Root":
    default:
      return {
        display: "flex",
        flexDirection: "column-reverse",
        gap: $theme.space[4]
      };
  }
};
const CIRCLE_BORDER_WIDTH = 3;
const ProgressBar = (_a) => {
  var _b = _a, { value = 0, maxValue = 100, overrides, $style } = _b, props = __objRest(_b, ["value", "maxValue", "overrides", "$style"]);
  const resolvedStyles = useResolvedStyle($style);
  if (props.variant === "circular") {
    const { diameter = 48 } = props;
    const progress = value / maxValue;
    const position = diameter / 2;
    const radius = (diameter - 2 * CIRCLE_BORDER_WIDTH) / 2;
    return jsx(ProgressBarRounded, { progress, overrides: mergeOverrides({
      Svg: {
        style: () => mergeStyles({
          width: `${diameter}px`,
          height: `${diameter}px`,
          transform: "rotate(270deg)"
        }, resolvedStyles),
        props: {
          viewBox: `0 0 ${diameter} ${diameter}`
        }
      },
      TrackBackground: {
        style: (styleProps) => mergeStyles({
          strokeLinecap: "round",
          strokeWidth: `${CIRCLE_BORDER_WIDTH}px`,
          stroke: styleProps.$theme.colors["borders-global-tertiary"]
        }),
        props: {
          d: `M${CIRCLE_BORDER_WIDTH},${position}a${radius},${radius} 0 1, 0 ${radius * 2},0a${radius},${radius} 0 1,0 -${radius * 2},0`
        }
      },
      TrackForeground: {
        style: (styleProps) => mergeStyles({
          strokeLinecap: "round",
          strokeWidth: `${CIRCLE_BORDER_WIDTH + 2}px`,
          stroke: styleProps.$theme.colors["background-brand-secondary"],
          strokeDashoffset: `${-styleProps.$pathLength * (1 - styleProps.$pathProgress)}`,
          transform: "rotate(180deg)",
          transformOrigin: "center"
        }),
        props: {
          d: `M${CIRCLE_BORDER_WIDTH},${position}a${radius},${radius} 0 1, 0 ${radius * 2},0a${radius},${radius} 0 1,0 -${radius * 2},0`
        }
      },
      Text: {
        style: ({ $theme }) => mergeStyles(__spreadProps(__spreadValues({}, $theme.typography.p4), { fontWeight: $theme.fontWeights.medium }))
      }
    }, overrides) });
  }
  const _a2 = props, { variant = "normal", showLabel = false, getProgressLabel = (v) => `${v}%` } = _a2, rest = __objRest(_a2, ["variant", "showLabel", "getProgressLabel"]);
  return jsx(ProgressBar$1, __spreadValues({ value, maxValue, getProgressLabel, showLabel: variant === "condensed" ? showLabel : false, overrides: mergeOverrides({
    Root: {
      style: (styleProps) => mergeStyles(getStyles(styleProps, "Root"), resolvedStyles)
    },
    BarContainer: {
      style: (styleProps) => getStyles(styleProps, "BarContainer")
    },
    Bar: {
      style: (styleProps) => getStyles(__spreadProps(__spreadValues({}, styleProps), { $variant: variant }), "Bar")
    },
    BarProgress: {
      style: (styleProps) => getStyles(__spreadProps(__spreadValues({}, styleProps), { $variant: variant, $showLabel: showLabel }), "BarProgress")
    },
    Label: {
      style: (styleProps) => getStyles(styleProps, "Label")
    }
  }, overrides) }, rest));
};

export { ProgressBar };
